import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle2,
  LogIn,
  Pencil,
  Plus,
  Trash2,
  XCircle,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import {
  useAdminPartners,
  useApprovePartner,
  useRejectPartner,
  useDeleteUser,
  useUpdatePartner,
  useOnboardPartner,
  useImpersonateUser,
} from "@/hooks/useAdminData";
import { useIndustries, useRegions } from "@/hooks/useReferenceData";
import { resolveAccountId } from "@/utils/adminRecords";
import { useAuthStore } from "@/store/authStore";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Field } from "@/components/forms";
import { PeopleAvatar, PeopleTooltipAction } from "@/components/admin/people-ui";

export const Route = createFileRoute("/admin/partners/")({
  component: AdminPartnersPage,
});

function AdminPartnersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const { user } = useAuthStore();

  const { data: partnersData = [], isLoading } = useAdminPartners();
  const approvePartner = useApprovePartner();
  const rejectPartner = useRejectPartner();
  const deleteUser = useDeleteUser();
  const impersonate = useImpersonateUser();
  const updatePartner = useUpdatePartner();
  const onboardPartner = useOnboardPartner();

  const { data: industriesData } = useIndustries();
  const { data: regions } = useRegions();

  const [editData, setEditData] = useState<Record<string, string>>({});

  // Onboard dialog form state
  const [onboardForm, setOnboardForm] = useState({
    name: "",
    email: "",
    password: "Partner@123",
    company_name: "",
    industry: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    location: "",
    website: "",
    description: "",
  });
  const [onboardLoading, setOnboardLoading] = useState(false);

  const filteredPartners = partnersData.filter((p: any) => {
    const name = p.name || "";
    const matchesSearch = name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (partner: any) => {
    setSelectedPartner(partner);
    setEditData({
      company_name: partner.name || "",
      industry: partner.industry || "",
      website: partner.website || "",
      location: partner.location || "",
      contact_person: partner.contact_person || "",
      contact_email: partner.contact_email || "",
      contact_phone: partner.contact_phone || "",
      description: partner.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (partner: any) => {
    setSelectedPartner(partner);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    const userId = resolveAccountId(selectedPartner);
    if (!userId) return;
    deleteUser.mutate(userId, { onSettled: () => setDeleteDialogOpen(false) });
  };

  const handleApprove = (partner: any) => {
    const userId = resolveAccountId(partner) || partner.user_id;
    if (!userId) return;
    approvePartner.mutate(userId);
  };

  const handleRejectClick = (partner: any) => {
    setSelectedPartner(partner);
    setRejectDialogOpen(true);
  };

  const confirmReject = () => {
    const userId = resolveAccountId(selectedPartner) || selectedPartner?.user_id;
    if (!userId) return;
    rejectPartner.mutate(userId, {
      onSettled: () => setRejectDialogOpen(false),
    });
  };

  const handleSaveEdit = () => {
    const userId = resolveAccountId(selectedPartner) || selectedPartner?.user_id;
    if (!userId) return;
    updatePartner.mutate(
      { id: userId, payload: editData },
      { onSettled: () => setEditDialogOpen(false) }
    );
  };

  const handleOnboard = async () => {
    setOnboardLoading(true);
    try {
      await onboardPartner.mutateAsync({
        ...onboardForm,
        // The backend expects 'name' as the account name
        name: onboardForm.name || onboardForm.company_name,
        // contact_email is used for the account login too
        email: onboardForm.email || onboardForm.contact_email,
      });
      setOnboardDialogOpen(false);
      setOnboardForm({
        name: "",
        email: "",
        password: "Partner@123",
        company_name: "",
        industry: "",
        contact_person: "",
        contact_email: "",
        contact_phone: "",
        location: "",
        website: "",
        description: "",
      });
    } catch (err) {
      console.error("Failed to onboard partner:", err);
    } finally {
      setOnboardLoading(false);
    }
  };

  const editField = (key: string, label: string, opts: { className?: string } = {}) => (
    <Field label={label} className={opts.className}>
      <Input
        value={editData[key] || ""}
        onChange={(e) => setEditData((d) => ({ ...d, [key]: e.target.value }))}
      />
    </Field>
  );

  const setOnboard = (key: keyof typeof onboardForm) => (value: string) =>
    setOnboardForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Institutional partners"
        subtitle="Manage industry partnerships, monitor placement capacity, and review organization health."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Partners" }]}
        action={
          <Button onClick={() => setOnboardDialogOpen(true)}>
            <Plus />
            Onboard partner
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-3">
        <StatCard label="Total network partners" value={partnersData.length} icon={<Building2 />} />
        <StatCard
          label="Verified organizations"
          value={partnersData.filter((p: any) => p.status === "active").length}
          icon={<CheckCircle2 />}
        />
        <StatCard
          label="Global placement slots"
          value={partnersData.reduce(
            (sum: number, p: any) => sum + (p.total_capacity || p.slots || 0),
            0,
          )}
          icon={<Briefcase />}
        />
      </div>

      <SectionCard
        title="Partner repository"
        description={`${filteredPartners.length} of ${partnersData.length} partners`}
        noPadding
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by name…"
              aria-label="Search partners by name"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-full sm:w-44" aria-label="Verification status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Verified</SelectItem>
                <SelectItem value="pending">Awaiting review</SelectItem>
                <SelectItem value="inactive">Restricted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : filteredPartners.length === 0 ? (
          <EmptyState
            bare
            icon={<Building2 />}
            title="No partners found"
            description={
              search || statusFilter !== "all"
                ? "Try a different search or status filter."
                : "Onboarded partner organizations will appear here."
            }
          />
        ) : (
          <Table className="min-w-[980px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Organization</TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="text-right">Capacity</TableHead>
                <TableHead>Deployment rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPartners.map((p: any) => {
                const slots = p.total_capacity || p.slots || 0;
                const filled = p.filled || 0;
                const fillRate = slots > 0 ? (filled / slots) * 100 : 0;
                const accountId = resolveAccountId(p);
                return (
                  <TableRow key={p.id} className="h-11">
                    <TableCell className="py-2 pl-4 md:pl-5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <PeopleAvatar name={p.name} />
                        <div className="min-w-0">
                          <Link
                            to={`/admin/partners/${p.id}` as any}
                            className="block max-w-[220px] truncate font-medium text-foreground hover:underline"
                          >
                            {p.name}
                          </Link>
                          <p className="max-w-[220px] truncate text-caption text-muted-foreground">
                            {p.email}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[160px] truncate py-2 text-body">
                      {p.industry || "-"}
                    </TableCell>
                    <TableCell className="py-2">
                      <p className="max-w-[180px] truncate text-foreground">
                        {p.contact_person || "-"}
                      </p>
                      {p.contact_phone && (
                        <p className="font-mono text-caption text-muted-foreground">
                          {p.contact_phone}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-foreground tabular-nums">
                      {filled}/{slots}
                    </TableCell>
                    <TableCell className="min-w-[160px] py-2">
                      <div className="flex items-center gap-2.5">
                        <Progress
                          value={fillRate}
                          className="h-1 flex-1"
                          aria-label={`${Math.round(fillRate)}% utility`}
                        />
                        <span className="w-9 text-right font-mono text-caption text-muted-foreground tabular-nums">
                          {Math.round(fillRate)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <StatusChip status={p.status} />
                    </TableCell>
                    <TableCell className="py-2 pr-4 md:pr-5">
                      <div className="flex items-center justify-end gap-0.5">
                        {p.status === "pending" && (
                          <>
                            <IconAction
                              label="Verify partner"
                              icon={<CheckCircle2 />}
                              className="hover:bg-success/10 hover:text-success"
                              onClick={() => handleApprove(p)}
                              disabled={approvePartner.isPending}
                            />
                            <IconAction
                              label="Reject partner"
                              tone="destructive"
                              icon={<XCircle />}
                              onClick={() => handleRejectClick(p)}
                              disabled={rejectPartner.isPending}
                            />
                          </>
                        )}
                        <PeopleTooltipAction
                          label={`Log in as ${p.company_name ?? p.name ?? "partner"}`}
                          tooltip={
                            accountId
                              ? "Open this partner's portal in a new tab"
                              : "This record has no linked account"
                          }
                          icon={<LogIn />}
                          disabled={impersonate.isPending || !accountId}
                          onClick={() =>
                            impersonate.mutate({
                              userId: resolveAccountId(p),
                              name: p.company_name ?? p.name ?? "this partner",
                            })
                          }
                        />
                        <IconAction
                          label="Update profile"
                          icon={<Pencil />}
                          onClick={() => handleEdit(p)}
                        />
                        <IconAction
                          label="Delete partner"
                          tone="destructive"
                          icon={<Trash2 />}
                          onClick={() => handleDelete(p)}
                        />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-foreground"
                          asChild
                        >
                          <Link
                            to={`/admin/partners/${p.id}` as any}
                            aria-label={`View ${p.name ?? "partner"}`}
                          >
                            <ArrowRight />
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {/* Edit partner dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Partner configuration</DialogTitle>
            <DialogDescription>
              Update the organization profile and contact details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {editField("company_name", "Organization name")}
            {editField("industry", "Industry")}
            {editField("contact_person", "Contact person")}
            {editField("contact_email", "Contact email")}
            {editField("contact_phone", "Contact phone")}
            {editField("website", "Website")}
            {editField("location", "Location", { className: "sm:col-span-2" })}
            <Field label="Description" className="sm:col-span-2">
              <Textarea
                rows={3}
                value={editData.description || ""}
                onChange={(e) => setEditData((d) => ({ ...d, description: e.target.value }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={updatePartner.isPending}
              loading={updatePartner.isPending}
              onClick={handleSaveEdit}
            >
              {updatePartner.isPending ? "Saving..." : "Commit changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <AlertTriangle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Delete partner</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">{selectedPartner?.name}</span>? The
              organization will lose access immediately. You can restore it from Trash.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteUser.isPending}
            >
              Keep active
            </Button>
            <Button
              variant="destructive"
              disabled={deleteUser.isPending}
              loading={deleteUser.isPending}
              onClick={confirmDelete}
            >
              {deleteUser.isPending ? "Deleting..." : "Delete now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject partner dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(o) => !o && setRejectDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mb-2 flex size-9 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive max-sm:mx-auto">
              <XCircle className="size-4" aria-hidden />
            </div>
            <DialogTitle>Reject partner</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject{" "}
              <span className="font-medium text-foreground">{selectedPartner?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectPartner.isPending}
              loading={rejectPartner.isPending}
              onClick={confirmReject}
            >
              {rejectPartner.isPending ? "Rejecting..." : "Reject now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboard new partner dialog */}
      <Dialog open={onboardDialogOpen} onOpenChange={(o) => !o && setOnboardDialogOpen(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Partner onboarding</DialogTitle>
            <DialogDescription>
              Enter the corporate details to establish a new institutional partnership.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <fieldset className="space-y-3">
              <legend className="eyebrow float-left mb-3 w-full">Account details</legend>
              <div className="clear-both grid gap-4 sm:grid-cols-2">
                <Field label="Account name">
                  <Input
                    placeholder="e.g. John Doe"
                    value={onboardForm.name}
                    onChange={(e) => setOnboard("name")(e.target.value)}
                  />
                </Field>
                <Field label="Login email">
                  <Input
                    type="email"
                    placeholder="admin@entity.co.tz"
                    value={onboardForm.email}
                    onChange={(e) => setOnboard("email")(e.target.value)}
                  />
                </Field>
                <Field label="Password">
                  <Input
                    value={onboardForm.password}
                    onChange={(e) => setOnboard("password")(e.target.value)}
                  />
                </Field>
              </div>
            </fieldset>

            <fieldset className="space-y-3 border-t border-border pt-5">
              <legend className="eyebrow float-left mb-3 w-full">Organization details</legend>
              <div className="clear-both grid gap-4 sm:grid-cols-2">
                <Field label="Legal entity name">
                  <Input
                    placeholder="e.g. CRDB Bank PLC"
                    value={onboardForm.company_name}
                    onChange={(e) => setOnboard("company_name")(e.target.value)}
                  />
                </Field>
                <Field label="Primary industrial sector">
                  {({ id }) => (
                    <Select
                      value={onboardForm.industry || undefined}
                      onValueChange={setOnboard("industry")}
                    >
                      <SelectTrigger id={id}>
                        <SelectValue placeholder="Select sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {industriesData?.map((ind: string | { id: number; name: string }) => (
                          <SelectItem
                            key={typeof ind === "string" ? ind : ind.id}
                            value={typeof ind === "string" ? ind : ind.name}
                          >
                            {typeof ind === "string" ? ind : ind.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Corporate website" className="sm:col-span-2">
                  <Input
                    placeholder="https://www.organization.co.tz"
                    value={onboardForm.website}
                    onChange={(e) => setOnboard("website")(e.target.value)}
                  />
                </Field>
              </div>
            </fieldset>

            <fieldset className="space-y-3 border-t border-border pt-5">
              <legend className="eyebrow float-left mb-3 w-full">Contact information</legend>
              <div className="clear-both grid gap-4 sm:grid-cols-3">
                <Field label="Focal point representative">
                  <Input
                    placeholder="Full name"
                    value={onboardForm.contact_person}
                    onChange={(e) => setOnboard("contact_person")(e.target.value)}
                  />
                </Field>
                <Field label="Official email">
                  <Input
                    type="email"
                    placeholder="hr@entity.co.tz"
                    value={onboardForm.contact_email}
                    onChange={(e) => setOnboard("contact_email")(e.target.value)}
                  />
                </Field>
                <Field label="Direct line">
                  <Input
                    placeholder="+255 7XX XXX XXX"
                    value={onboardForm.contact_phone}
                    onChange={(e) => setOnboard("contact_phone")(e.target.value)}
                  />
                </Field>
                <Field label="Operating region" className="sm:col-span-2">
                  {({ id }) => (
                    <Select
                      value={onboardForm.location || undefined}
                      onValueChange={setOnboard("location")}
                    >
                      <SelectTrigger id={id}>
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions?.map((r: string) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            </fieldset>

            <fieldset className="border-t border-border pt-5">
              <legend className="sr-only">Strategic overview</legend>
              <Field label="Strategic overview">
                <Textarea
                  rows={3}
                  placeholder="Brief description of the organization and partnership goals..."
                  value={onboardForm.description}
                  onChange={(e) => setOnboard("description")(e.target.value)}
                />
              </Field>
            </fieldset>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOnboardDialogOpen(false)}>
              Cancel
            </Button>
            <Button disabled={onboardLoading} loading={onboardLoading} onClick={handleOnboard}>
              {onboardLoading ? "Creating..." : "Finalize partnership"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
